import React, { useEffect, useState } from 'react'
import {
  MotifButton,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
} from '@ey-xd/motif-react'
import './devModal.scss'
import {
  useLazyGetDevOpsQuery,
  useSaveDevOpsMutation,
} from '@/services/devops/devopsReducer'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

type DevOpsModalProps = {
  isOpen: boolean
  setISOpen: any
  backBtnHandler: (event: React.MouseEvent<HTMLButtonElement>) => void
  nextBtnHandler: (selectedOption: string) => void
  heading?: string
  message?: string
  loading?: boolean
  devOpsId?: string | null
}

export default function DevOpsModal({
  isOpen,
  setISOpen,
  nextBtnHandler,
  devOpsId: initialDevOpsId,
}: DevOpsModalProps) {
  const [selectedOptionStep1, setSelectedOptionStep1] = useState('')
  const [selectedOptionStep2, setSelectedOptionStep2] = useState('')
  const [descriptionStep1, setDescriptionStep1] = useState('')
  const [descriptionStep2, setDescriptionStep2] = useState('')
  const [devOpsId, setDevOpsId] = useState(initialDevOpsId)
  const [isDevOpsUsed, setIsDevOpsUsed] = useState<boolean | false>(false)
  const [isDevProdAccess, setIsDevProdAccess] = useState<boolean>(false)
  const { engagementId } = useParams<{ engagementId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [saveDevOps, { isLoading: isSaving }] = useSaveDevOpsMutation()
  const [
    triggerGetDevOps,
    { data: devOpsData, isLoading: isFetching, isError },
  ] = useLazyGetDevOpsQuery()

  const step1Options = [
    {
      value: 'true',
      label: 'Yes',
      description: '',
    },
    {
      value: 'false',
      label: 'No',
      description:
        'Please proceed with utilizing the tool to conduct your SOD analysis.',
    },
  ]

  const step2Options = [
    {
      value: 'true',
      label: 'Yes',
      description:
        'In this case, please proceed with documenting your analysis as per your business per usual approach.',
    },
    {
      value: 'false',
      label: 'No',
      description:
        'Please proceed with utilizing the tool to conduct your SOD analysis.',
    },
  ]

  useEffect(() => {
    if (searchParams.get('showDevModal') === 'true') {
      setISOpen(true)
    }
  }, [searchParams, setISOpen])

  useEffect(() => {
    if (isOpen && engagementId) {
      triggerGetDevOps({ engagementId })
        .unwrap()
        .then((data) => {
          if (data?.id) {
            setDevOpsId(data.id)
          }
          setIsDevOpsUsed(data?.isDevOpsUsed ?? false)
          setIsDevProdAccess(data?.isDevProdAccess ?? false)
          const step1Value = data?.isDevOpsUsed ? 'true' : 'false'
          const step2Value = data?.isDevProdAccess ? 'true' : 'false'
          setSelectedOptionStep1(step1Value)
          setSelectedOptionStep2(step2Value)
          const step1Desc =
            step1Options.find((opt) => opt.value === step1Value)?.description ||
            ''
          setDescriptionStep1(step1Desc)
          const step2Desc =
            step2Options.find((opt) => opt.value === step2Value)?.description ||
            ''
          setDescriptionStep2(step2Desc)
        })
        .catch((error) => {
          if (error.status === 404) {
            setIsDevOpsUsed(false)
            setIsDevProdAccess(false)
            setSelectedOptionStep1('false')
            setSelectedOptionStep2('false')
            setDescriptionStep1(
              step1Options.find((opt) => opt.value === 'false')?.description ||
                '',
            )
            setDescriptionStep2(
              step2Options.find((opt) => opt.value === 'false')?.description ||
                '',
            )
          } else {
            console.error('Error fetching DevOps data:', error)
          }
        })
    }
  }, [isOpen, engagementId, triggerGetDevOps])

  const handleOptionChangeStep1 = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value
    setSelectedOptionStep1(value)

    const selectedOption = step1Options.find((option) => option.value === value)
    if (selectedOption) {
      setDescriptionStep1(selectedOption.description)
    }
  }

  const handleOptionChangeStep2 = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value
    setSelectedOptionStep2(value)

    const selectedOption = step2Options.find((option) => option.value === value)
    if (selectedOption) {
      setDescriptionStep2(selectedOption.description)
    }
  }

  const handleSave = async () => {
    try {
      const data = {
        ...(devOpsId && { id: devOpsId }),
        isDevOpsUsed: selectedOptionStep1 === 'true',
        isDevProdAccess: selectedOptionStep2 === 'true',
      }

      const response = await saveDevOps({
        engagementId: engagementId,
        data: data,
      }).unwrap()

      if (response?.id) {
        setDevOpsId(response.id)
      }

      if (selectedOptionStep1 === 'false') {
        console.log('Redirecting to IT SOD analysis page...')
        setISOpen(false)
        return
      } else if (selectedOptionStep2 === 'false') {
        console.log('Redirecting to another page...')
        setISOpen(false)
        return
      } else if (selectedOptionStep2 === 'true') {
        console.log('Redirecting to another page...')
        navigate(`/`)
        return
      }
      nextBtnHandler(selectedOptionStep2)
    } catch (error) {
      console.error('Error saving DevOps:', error)
    }
  }

  return (
    <>
      <MotifModal
        onClose={() => setISOpen(false)}
        title="Question"
        show={isOpen}
        size="md"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
      >
        <MotifModalBody>
          <p>DevOps is a set of practices that integrates software development (Dev) and IT operations (Ops) to enhance collaboration and productivity. In a DevOps environment, developers often have direct access to production systems, which can create potential Segregation of Duties (SoD) conflicts; however, management typically implements additional checks and controls to monitor changes. If DevOps is not used, please proceed with using the tool. </p>
          <p><span style={{ fontWeight: 800 }}>Is DevOps used?</span>
          {step1Options.map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name="step1Option"
                value={option.value}
                checked={selectedOptionStep1 === option.value}
                onChange={handleOptionChangeStep1}
              />
              {option.label}
            </label>
          ))}
          </p>
          {descriptionStep1 && (
            <p className="description-text">{descriptionStep1}</p>
          )}

          {selectedOptionStep1 === 'true' && (
            <>
            <p>
              <span style={{ fontWeight: 800 }}>Do developers have access to PROD?</span>
              {step2Options.map((option) => (
                <label key={option.value}>
                  <input
                    type="radio"
                    name="step2Option"
                    value={option.value}
                    checked={selectedOptionStep2 === option.value}
                    onChange={handleOptionChangeStep2}
                  />
                  {option.label}
                </label>
              ))}
              </p>
              {descriptionStep2 && (
                <p className="description-text Italic">{descriptionStep2}</p>
              )}
            </>
          )}
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            className="previous-button"
            onClick={() => navigate('/')}
          >
            Back
          </MotifButton>
          <MotifButton
            variant="primary"
            className="next-button"
            onClick={handleSave}
            disabled={
              !selectedOptionStep1 ||
              (selectedOptionStep1 === 'true' && !selectedOptionStep2)
            }
          >
            Continue
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}
